/**
 * Top-level error boundary so we never silently white-screen.
 *
 * Without this, any uncaught render error inside the route tree results in
 * React unmounting *everything* and rendering nothing — which is exactly the
 * "blank white page" the user was reporting. With this boundary in place we
 * always show *something*, plus the error details so we can debug.
 */
import React from 'react';

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info);
    this.setState({ info });
    // Expose to window for screenshot/debug tools.
    try {
      window.__lastReactError = {
        message: String(error?.message || error),
        stack: String(error?.stack || ''),
        component: String(info?.componentStack || ''),
      };
    } catch { /* noop */ }
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  handleLogout = () => {
    try {
      localStorage.removeItem('savenes_token');
      localStorage.removeItem('savenes_original_admin');
      localStorage.removeItem('savenes_impersonating');
    } catch { /* noop */ }
    window.location.href = '/login';
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{
        minHeight: '100vh',
        background: '#FEF2F2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: 'system-ui, sans-serif',
      }}>
        <div style={{
          maxWidth: 720,
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #FCA5A5',
          padding: 32,
          boxShadow: '0 10px 25px rgba(0,0,0,0.06)',
        }}>
          <h1 style={{ margin: 0, color: '#991B1B', fontSize: 22 }}>
            Something went wrong loading this page
          </h1>
          <p style={{ marginTop: 12, color: '#4B5563' }}>
            We hit an unexpected error rendering this view. Try going back to
            the dashboard, or sign out and back in. If this keeps happening,
            copy the details below and share them with support.
          </p>
          <div style={{ marginTop: 18, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => { this.handleReset(); window.location.href = '/dashboard'; }}
              style={{ background: '#0E7490', color: '#fff', border: 0, padding: '8px 14px', borderRadius: 8, cursor: 'pointer' }}
              data-testid="error-go-dashboard">
              Go to dashboard
            </button>
            <button
              onClick={this.handleLogout}
              style={{ background: '#F3F4F6', color: '#0A2342', border: '1px solid #E5E7EB', padding: '8px 14px', borderRadius: 8, cursor: 'pointer' }}
              data-testid="error-sign-out">
              Sign out
            </button>
          </div>
          <details style={{ marginTop: 24 }}>
            <summary style={{ cursor: 'pointer', color: '#6B7280', fontSize: 13 }}>
              Show error details
            </summary>
            <pre style={{
              marginTop: 8, padding: 12, background: '#F9FAFB',
              border: '1px solid #E5E7EB', borderRadius: 8,
              fontSize: 11, color: '#374151', overflow: 'auto', maxHeight: 360,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
{String(this.state.error?.message || this.state.error)}

{this.state.error?.stack || ''}

{this.state.info?.componentStack || ''}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
